import Image from "next/image";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Hero carousel slide 3 — the CRM customer profile, assembled from the four
 * exports the comp ships it as rather than one flattened shot, so each piece
 * can arrive on its own beat and keep bobbing afterwards.
 *
 * Layout is the comp's second pass on this slide: the tablet leans right and
 * bleeds off the column, the tier card sits in front of its top bezel, the
 * profile card hangs off the composition's left edge and the insight panel
 * rides the tablet's right half. Everything is placed in percentages of one
 * frame box, measured off the comp, so the arrangement scales as one unit;
 * on lg the frame takes the stage's full 800px height (aspect drives width),
 * on smaller screens it falls back to width-driven.
 *
 * The tablet export is cut mid-screen at its foot — in the comp the fold
 * crops it, here a bottom fade dissolves it the way slide 2's dashboard
 * handles its own cropped edges.
 */
type Piece = {
  src: string;
  /** Key under `home.mocks.alt`. */
  altKey: string;
  w: number;
  h: number;
  /** Placement in % of the comp frame. */
  pos: string;
  /** Rendered width for the srcset pick — the piece's own share of the frame
      (frame ≈ 90vw of a phone, ≈ 57vw of a 1440 desktop), not one blanket
      figure. The old shared "50vw" told the browser the two ~27vw cards were
      twice their real size, and mobile Lighthouse charged ~43 KiB for it. */
  sizes: string;
  /** Entrance: which class, and how late. Spaced about a beat apart, like
      slide 1's notification cards, so the scene is still assembling for most
      of the reel's 3s hold — the last piece plus its 0.5s entrance has to
      settle inside SLIDE_MS (hero-carousel.tsx). */
  anim: string;
  delay: string;
  /** Idle bob offset, so the cards never move in lockstep. */
  bob?: string;
  z: string;
};

const PIECES: Piece[] = [
  {
    src: "/v2/hero-tier-card.png",
    altKey: "tierCard",
    w: 1644,
    h: 648,
    pos: "left-[5.1%] top-[4.7%] w-[54.5%]",
    sizes: "(max-width: 1023px) 50vw, 31vw",
    anim: "hero-piece-left",
    delay: "0.5s",
    bob: "-0.9s",
    // Behind the tablet (z-[2]) — its right end disappears under the bezel.
    z: "z-[1]",
  },
  {
    src: "/v2/hero-insight-panel.png",
    altKey: "insightPanel",
    w: 1030,
    h: 1230,
    pos: "left-[60.7%] top-[32.8%] w-[29.8%]",
    sizes: "(max-width: 1023px) 27vw, 17vw",
    anim: "hero-piece-right",
    delay: "1.15s",
    bob: "-2.1s",
    z: "z-[3]",
  },
  {
    src: "/v2/hero-profile-card.png",
    altKey: "profileCard",
    w: 1022,
    h: 1110,
    pos: "left-[0.6%] top-[48.3%] w-[28.6%]",
    sizes: "(max-width: 1023px) 26vw, 17vw",
    anim: "hero-piece-left",
    delay: "1.8s",
    bob: "-3.4s",
    z: "z-[3]",
  },
];

export default function CrmProfile() {
  const { t } = useTranslation();

  return (
    <div className="relative h-full w-full">
      {/* The comp frame all percentages refer to. Height-driven on lg, where
          the stage owns the fold height; width-driven below, and full width
          under sm because the stage takes this frame's own ratio there.
          Leans right with a slight overhang like the comp — the section's
          overflow-hidden crops the bleed at the viewport edge. */}
      <div className="relative ml-auto aspect-[804/788] w-full sm:w-[88%] lg:mr-[-4%] lg:h-full lg:w-auto">
        <Image
          src="/v2/hero-crm-tablet.png"
          alt={t("home.mocks.alt.crmTablet")}
          width={1764}
          height={2288}
          quality={90}
          sizes="(max-width: 1023px) 60vw, 34vw"
          className="hero-piece-rise absolute left-[17.5%] top-[21.7%] z-[2] h-auto w-[64.8%]"
          style={{
            maskImage:
              "linear-gradient(to bottom, #000 72%, transparent 90%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, #000 72%, transparent 90%)",
          }}
        />

        {PIECES.map((p) => (
          <div
            key={p.src}
            className={`absolute ${p.z} ${p.pos} ${p.anim}`}
            style={{ animationDelay: p.delay }}
          >
            <div className="jd-float" style={{ animationDelay: p.bob }}>
              <Image
                src={p.src}
                alt={t(`home.mocks.alt.${p.altKey}`)}
                width={p.w}
                height={p.h}
                quality={90}
                sizes={p.sizes}
                className="h-auto w-full"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
